import { workLine } from "./foodView";
import type { WorkParts } from "./today";
import type { CycleInfo, KayaState, StepBasis } from "./types";

/**
 * Small pure readings the Today and Activity screens share. They sit here rather than in the
 * pages so the wording that states provenance ("phone", "est.") is decided once and tested.
 */

export type StepTone = "phone" | "logged" | "est";

/**
 * The Tag a step count carries. Anything the phone counted is "phone", even with a treadmill
 * estimate added, because the phone is most of it; a hand count is "logged"; the treadmill's
 * distance-over-stride figure alone is "est.". Nothing counted has no tag.
 */
export function stepTone(basis: StepBasis): StepTone | null {
  switch (basis) {
    case "none":
      return null;
    case "estimate-only":
      return "est";
    case "manual-plus-estimate":
      return "logged";
    default:
      return "phone";
  }
}

/** "Where the count comes from", one sentence per basis. */
export const STEP_BASIS_SENTENCE: Record<StepBasis, string> = {
  none: "Nothing counted yet today. Kaya Steps on your phone fills this in, or add a count by hand.",
  "estimate-only": "An estimate from today's treadmill session: its distance over your stride.",
  "manual-plus-estimate": "The count you typed by hand, plus the treadmill session's estimate.",
  "device-only":
    "Your phone’s own step counter, read by Kaya Steps. A count you type by hand is kept apart and never overwritten.",
  "device-plus-estimate":
    "Your phone’s counter, plus the treadmill session's estimate, because the phone sat on the console.",
  "device-measured":
    "Your phone’s counter. It was on you for the treadmill walk, so only what it missed is estimated.",
  "device-conservative":
    "Your phone’s counter, or your hand count plus the treadmill estimate if that is higher. Nothing is counted twice.",
};

/** The moves ticked off, from `sessionPct`'s fraction. */
export function movesDone(pct: number, moves: number): number {
  return Math.max(0, Math.min(moves, Math.round(pct * moves)));
}

/**
 * "182 session + 96 treadmill + 41 walk = ~319 kcal est." — the sum is spelled out because "319"
 * alone invites "but I did two things", and the answer is that both are in there.
 * Null when nothing was logged (invariant 11).
 */
export function workSentence(w: WorkParts): string | null {
  const line = workLine(w);
  if (w.total === null || line === null) return null;
  return `${line} = ~${w.total.toLocaleString()} kcal est.`;
}

export type CycleVariant = "period" | "expected" | "pms" | "unknown" | "normal";

/** Which of the five cycle states today is in; the order is Home's, first match wins. */
export function cycleVariant(ci: CycleInfo): CycleVariant {
  if (ci.inPeriod) return "period";
  if (ci.predicted) return "expected";
  if (ci.pms) return "pms";
  if (ci.day === null) return "unknown";
  return "normal";
}

